// src/odoo/customers/pages/CustomerDetailPage.tsx
// All comments are in English.

import React, { useMemo, useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  useCustomerContacts,
  useCustomerOverview,
  usePaymentSummary,
  usePurchaseTrend,
  useCustomerDocuments,
} from "../hooks";
import {
  fetchCustomerCompanies,
  type CustomerCompanyListItem,
} from "../api/customerListApi";
import type { DateRangeQuery } from "../api/overviewApi";
import { CustomerOverviewCards } from "../components/CustomerOverviewCards";
import { CustomerPurchaseTrendChart } from "../components/CustomerPurchaseTrendChart";
import { CustomerPaymentAging } from "../components/CustomerPaymentAging";
import { CustomerDocumentsSection } from "../components/CustomerDocumentsSection";
import { CustomerContactsSection } from "../components/CustomerContactsSection";
import { CustomerProductInsights } from "../components/CustomerProductInsights";
import { CustomerProductRecommendations } from "../components/CustomerProductRecommendations";
import { CustomerLicenseOverview } from "../components/CustomerLicenseOverview";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from "@/components/ui/command";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Check, ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/utils";

// Odoo web base url for opening the customer form
const ODOO_WEB_BASE_URL = import.meta.env.VITE_ODOO_WEB_URL;
/**
 * Build Odoo url for the given customer/company id.
 * Note: {id} should be the same as /odoo/customer/{id}.
 */
const buildOdooCustomerUrl = (id?: number) => {
  if (!id) return null;
  return `${ODOO_WEB_BASE_URL}#id=${id}&cids=1-2-3&menu_id=306&action=484&model=res.partner&view_type=form`;
};

/**
 * Utility to format Date to YYYY-MM-DD for the API.
 */
const formatDate = (date: Date): string => {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
};

/**
 * Returns default date range (last 12 months) for the filters.
 */
const getDefaultDateRange = (): DateRangeQuery => {
  const to = new Date();
  const from = new Date();
  from.setFullYear(to.getFullYear() - 1);
  from.setDate(1);

  return {
    date_from: formatDate(from),
    date_to: formatDate(to),
  };
};

export const CustomerDetailPage: React.FC = () => {
  const params = useParams<{ customerId: string }>();
  const navigate = useNavigate();

  // Initial company id from URL.
  const initialCompanyId = useMemo(
    () => (params.customerId ? Number(params.customerId) : undefined),
    [params.customerId]
  );

  const [selectedCompanyId, setSelectedCompanyId] = useState<
    number | undefined
  >(initialCompanyId);
  const [dateRange, setDateRange] =
    useState<DateRangeQuery>(getDefaultDateRange);

  const [isCompanyPopoverOpen, setIsCompanyPopoverOpen] =
    useState<boolean>(false);

  const [activeTab, setActiveTab] = useState<
    "license-overview" | "overview" | "product-insights" | "sales-invoices" | "product-reco"
  >("overview");

  // Company dropdown state (search + data)
  const [companySearchInput, setCompanySearchInput] = useState<string>("");
  const [companySearchTerm, setCompanySearchTerm] = useState<string>("");
  const [companies, setCompanies] = useState<CustomerCompanyListItem[]>([]);
  const [isCompaniesLoading, setIsCompaniesLoading] = useState<boolean>(false);
  const [companiesError, setCompaniesError] = useState<string | null>(null);

  // When customer changes, update URL (stay under /odoo/customer).
  useEffect(() => {
    if (selectedCompanyId) {
      navigate(`/odoo/customer/${selectedCompanyId}`, { replace: true });
    }
  }, [selectedCompanyId, navigate]);

  // --- Data hooks for the selected company ---

  const {
    data: overview,
    isLoading: isOverviewLoading,
    error: overviewError,
  } = useCustomerOverview(selectedCompanyId, dateRange);

  const {
    data: paymentSummary,
    isLoading: isPaymentLoading,
    error: paymentError,
  } = usePaymentSummary(selectedCompanyId, dateRange);

  const {
    data: documents,
    isLoading: isDocumentsLoading,
    error: documentsError,
  } = useCustomerDocuments(selectedCompanyId, {
    ...dateRange,
    limit_sales_orders: 50,
    limit_invoices: 50,
  });

  const {
    data: purchaseTrend,
    isLoading: isTrendLoading,
    error: trendError,
  } = usePurchaseTrend(selectedCompanyId, "day", dateRange);

  const {
    data: contacts,
    isLoading: isContactsLoading,
    error: contactsError,
  } = useCustomerContacts(selectedCompanyId, dateRange);

  const accessDenied = useMemo(
    () =>
      [overviewError, paymentError, documentsError, trendError, contactsError].some(
        (err) => (err as any)?.status === 403
      ),
    [overviewError, paymentError, documentsError, trendError, contactsError]
  );

  // --- Company dropdown: debounce search input -> search term ---

  useEffect(() => {
    const handle = window.setTimeout(() => {
      setCompanySearchTerm(companySearchInput.trim());
    }, 300);

    return () => window.clearTimeout(handle);
  }, [companySearchInput]);

  useEffect(() => {
    if (accessDenied) {
      navigate("/odoo/customer", { replace: true });
    }
  }, [accessDenied, navigate]);

  // --- Company dropdown: call /customers/company with q + full list (page_size=200) ---

  useEffect(() => {
    let cancelled = false;

    const loadCompanies = async () => {
      setIsCompaniesLoading(true);
      setCompaniesError(null);
      try {
        const res = await fetchCustomerCompanies({
          q: companySearchTerm || undefined,
          page: 1,
          page_size: 200,
        });
        if (cancelled) return;
        setCompanies(res.items ?? []);
      } catch (error) {
        console.error("Failed to load companies", error);
        if (cancelled) return;
        setCompaniesError("Failed to load companies");
        setCompanies([]);
      } finally {
        if (!cancelled) {
          setIsCompaniesLoading(false);
        }
      }
    };

    loadCompanies();

    return () => {
      cancelled = true;
    };
  }, [companySearchTerm]);

  // Prefer company name from overview API; fallback to company list.
  const selectedCompanyName = useMemo(() => {
    if (overview && (overview as any).company_name) {
      return (overview as any).company_name;
    }

    if (!selectedCompanyId) {
      return "Customer";
    }

    const found = companies.find(
      (c) => c.company_partner_id === selectedCompanyId
    );
    return found?.company_name ?? `Company #${selectedCompanyId}`;
  }, [overview, companies, selectedCompanyId]);

  // Derived flags.
  const isAnyMainLoading =
    isOverviewLoading || isPaymentLoading || isTrendLoading;

  const isCompanySelectorDisabled =
    isCompaniesLoading || !!companiesError || companies.length === 0;

  const isCompanySelected = !!selectedCompanyId;
  const showDateRangeFilter = activeTab !== "license-overview";

  /**
   * Handle selecting a company from the dropdown.
   */
  const handleSelectCompany = (company: CustomerCompanyListItem) => {
    const id = company.company_partner_id;
    if (typeof id === "number") {
      setSelectedCompanyId(id);
    }
    setIsCompanyPopoverOpen(false);
  };

  const handleDateFromChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setDateRange((prev) => ({
      ...prev,
      date_from: value || prev.date_from,
    }));
  };

  const handleDateToChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setDateRange((prev) => ({
      ...prev,
      date_to: value || prev.date_to,
    }));
  };

  const renderOverviewSkeleton = () => (
    <div className="space-y-4">
      <div className="grid gap-4 md:grid-cols-3">
        <div className="rounded-lg border bg-card p-4">
          <Skeleton className="mb-2 h-4 w-1/3" />
          <Skeleton className="h-6 w-1/2" />
        </div>
        <div className="rounded-lg border bg-card p-4">
          <Skeleton className="mb-2 h-4 w-1/3" />
          <Skeleton className="h-6 w-1/2" />
        </div>
        <div className="rounded-lg border bg-card p-4">
          <Skeleton className="mb-2 h-4 w-1/3" />
          <Skeleton className="h-6 w-1/2" />
        </div>
      </div>
      <div className="flex justify-center py-10">
        <p className="text-sm text-muted-foreground">
          Loading customer data...
        </p>
      </div>
    </div>
  );

  const renderNoCompanySelected = () => (
    <div className="flex h-40 flex-col items-center justify-center gap-2 rounded-lg border border-dashed bg-muted/40 text-center">
      <p className="text-sm font-medium">No customer selected</p>
      <p className="max-w-md text-xs text-muted-foreground">
        Use the Company selector above to choose a customer and see their
        overview, documents, and product insights.
      </p>
    </div>
  );

  const handleOpenInOdoo = () => {
    if (!selectedCompanyId) return;
    const url = buildOdooCustomerUrl(selectedCompanyId);
    if (!url) return;

    // Open Odoo in a new tab
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="space-y-6">
      {/* Header with customer selector and date range filter */}
      <header className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            {selectedCompanyName}
          </h1>
          <p className="text-sm text-muted-foreground">
            Customer detail overview
          </p>
          {(overview as any)?.salesperson_name && (
            <p className="mt-1 text-sm text-muted-foreground">
              Salesperson:{" "}
              <span className="font-bold text-emerald-600">
                {(overview as any).salesperson_name}
              </span>
            </p>
          )}
        </div>

        <div className="flex flex-col gap-3 md:flex-row md:items-end md:gap-6">
          {/* Company selector with full list + type-to-search (Combobox) */}
          <div className="flex min-w-[320px] flex-col">
            <label className="mb-1 text-xs font-medium text-muted-foreground">
              Company
            </label>
            <Popover
              open={isCompanyPopoverOpen}
              onOpenChange={setIsCompanyPopoverOpen}
            >
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={isCompanyPopoverOpen}
                  className={cn(
                    "h-9 w-full justify-between text-sm",
                    isCompanySelectorDisabled && "cursor-not-allowed opacity-70"
                  )}
                  disabled={isCompanySelectorDisabled}
                >
                  {isCompaniesLoading
                    ? "Loading companies..."
                    : companiesError
                    ? "Failed to load companies"
                    : selectedCompanyId
                    ? selectedCompanyName
                    : "Select company"}
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent
                className="min-w-[320px] w-auto p-0"
                align="start"
              >
                <Command>
                  <CommandInput
                    placeholder="Search company..."
                    className="h-9 text-xs"
                    value={companySearchInput}
                    onValueChange={setCompanySearchInput}
                  />
                  <CommandEmpty>No company found.</CommandEmpty>
                  <CommandGroup>
                    {companies.map((c) => {
                      const id = c.company_partner_id;
                      const name = c.company_name ?? `Company #${id}`;
                      const isSelected = id === selectedCompanyId;
                      return (
                        <CommandItem
                          key={`${id}-${name}`}
                          value={`${name} #${id}`}
                          onSelect={() => handleSelectCompany(c)}
                          className="flex items-center justify-between"
                        >
                          <div className="flex items-center gap-2">
                            <Check
                              className={cn(
                                "h-4 w-4",
                                isSelected
                                  ? "opacity-100"
                                  : "opacity-0"
                              )}
                            />
                            <span className="truncate">{name}</span>
                          </div>
                          <span className="ml-2 text-[11px] text-muted-foreground">
                            #{id}
                          </span>
                        </CommandItem>
                      );
                    })}
                  </CommandGroup>
                </Command>
              </PopoverContent>
            </Popover>
          </div>

          {showDateRangeFilter ? (
            <div className="flex flex-col">
              <span className="mb-1 text-xs font-medium text-muted-foreground">
                Date range
              </span>
              <div className="flex items-end gap-2">
                <div className="flex flex-col">
                  <span className="mb-0.5 text-[11px] text-muted-foreground">
                    From
                  </span>
                  <input
                    type="date"
                    className="h-9 rounded-md border bg-background px-2 text-xs shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                    value={dateRange.date_from}
                    onChange={handleDateFromChange}
                    max={dateRange.date_to}
                  />
                </div>
                <span className="mb-1 text-xs text-muted-foreground">to</span>
                <div className="flex flex-col">
                  <span className="mb-0.5 text-[11px] text-muted-foreground">
                    To
                  </span>
                  <input
                    type="date"
                    className="h-9 rounded-md border bg-background px-2 text-xs shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                    value={dateRange.date_to}
                    onChange={handleDateToChange}
                    min={dateRange.date_from}
                  />
                </div>
              </div>
            </div>
          ) : null}

          {/* Open in Odoo button */}
          <div className="flex flex-col">
            <span className="mb-1 text-xs font-medium text-muted-foreground">
              &nbsp;
            </span>
            <Button
              className="h-9"
              variant="outline"
              disabled={!isCompanySelected}
              onClick={handleOpenInOdoo}
            >
              Open in Odoo
            </Button>
          </div>
        </div>
      </header>

      {/* Tabs for overview, product insights and sales/invoices */}
      <Tabs
        value={activeTab}
        onValueChange={(v) =>
          setActiveTab(
            v as "license-overview" | "overview" | "product-insights" | "sales-invoices" | "product-reco"
          )
        }
        className="space-y-4"
      >
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="license-overview">License overview</TabsTrigger>
          <TabsTrigger value="sales-invoices">Sales &amp; invoices</TabsTrigger>
          <TabsTrigger value="product-insights">Product insights</TabsTrigger>
          <TabsTrigger value="product-reco">Cross-sell</TabsTrigger>
        </TabsList>

        {/* License Overview tab */}
        <TabsContent value="license-overview" className="space-y-4">
          {!isCompanySelected ? (
            renderNoCompanySelected()
          ) : (
            <CustomerLicenseOverview
              companyName={selectedCompanyName}
              companyPartnerId={selectedCompanyId}
            />
          )}
        </TabsContent>

        {/* Overview tab */}
        <TabsContent value="overview" className="space-y-6">
          {!isCompanySelected ? (
            renderNoCompanySelected()
          ) : isAnyMainLoading ? (
            renderOverviewSkeleton()
          ) : (
            <>
              {/* Overview cards */}
              <CustomerOverviewCards
                overview={overview}
                isLoading={isOverviewLoading}
                error={overviewError}
                dateRange={dateRange}
              />

              {/* Charts: Purchase trend & payment aging */}
              <section className="grid grid-cols-1 gap-6 lg:grid-cols-[2fr_1fr]">
                <div className="w-full">
                  <CustomerPurchaseTrendChart
                    data={purchaseTrend}
                    isLoading={isTrendLoading}
                    error={trendError}
                  />
                </div>

                <div className="w-full">
                  <CustomerPaymentAging
                    data={paymentSummary}
                    isLoading={isPaymentLoading}
                    error={paymentError}
                    invoices={documents?.invoices}
                    isInvoicesLoading={isDocumentsLoading}
                  />
                </div>
              </section>

              {/* Contacts */}
              <div className="grid gap-4 grid-cols-1">
                <CustomerContactsSection
                  data={contacts}
                  isLoading={isContactsLoading}
                  error={contactsError}
                />
              </div>
            </>
          )}
        </TabsContent>

        {/* Product Insights tab */}
        <TabsContent value="product-insights" className="space-y-4">
          {!isCompanySelected ? (
            renderNoCompanySelected()
          ) : (
            <CustomerProductInsights
              companyPartnerId={selectedCompanyId}
              dateRange={dateRange}
            />
          )}
        </TabsContent>

        {/* Sales & Invoices tab */}
        <TabsContent value="sales-invoices" className="space-y-4">
          {!isCompanySelected ? (
            renderNoCompanySelected()
          ) : (
            <CustomerDocumentsSection
              data={documents}
              isLoading={isDocumentsLoading}
              error={documentsError}
            />
          )}
        </TabsContent>

        {/* Cross-sell tab */}
        <TabsContent value="product-reco" className="space-y-4">
          {!isCompanySelected ? (
            renderNoCompanySelected()
          ) : (
            <CustomerProductRecommendations
              companyPartnerId={selectedCompanyId}
            />
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default CustomerDetailPage;
